import * as Sentry from '@sentry/react';

/**
 * What ended a mint without a token:
 * - `site-key-missing`: the app has no Turnstile site key configured;
 * - `script-unavailable`: Cloudflare's script did not load (blocked, offline, timed out);
 * - `deadline`: a speculative mint gave way after its short deadline;
 * - `escalation-declined`: a speculative mint was asked for a human challenge and declined it;
 * - `challenge-unresolved`: the challenge failed, expired or ran out of time.
 */
export type TurnstileUnavailableReason =
    | 'site-key-missing'
    | 'script-unavailable'
    | 'deadline'
    | 'escalation-declined'
    | 'challenge-unresolved';

const reported = new Set<string>();

/**
 * Leave a warning when a mint ends without a token (CUR-1962).
 *
 * The request then goes out with no credential and the server refuses it. The server keeps that
 * refusal out of Sentry, the verifier reports nothing for a missing token, and the cache layer no
 * longer re-files the refusal — so without this, a browser that cannot obtain a challenge token
 * is invisible. One issue for all of them, tagged with what ended the mint, at most once per
 * page load for each reason and priority: a claimant whose browser blocks the vendor script
 * would otherwise send one per checkpoint.
 */
export function reportTurnstileTokenUnavailable({
    action,
    priority,
    reason,
}: {
    action: string;
    priority: 'foreground' | 'background';
    reason: TurnstileUnavailableReason;
}) {
    const key = `${priority}:${reason}`;
    if (reported.has(key)) return;
    reported.add(key);

    Sentry.captureMessage('Turnstile token unavailable', {
        level: 'warning',
        fingerprint: ['turnstile-token-unavailable'],
        tags: {
            turnstileReason: reason,
            turnstilePriority: priority,
            turnstileAction: action,
        },
    });
}

/** Test hook: forget what this page load already reported. */
export function resetTurnstileUnavailableReportForTests() {
    reported.clear();
}
